import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { prefersReducedMotion } from '../../utils/scroll'
import './Effects.scss'

const pageTitles = {
  '/': 'Paradzai Gwira · AI Software Engineer',
  '/services': 'Services · Paradzai Gwira',
  '/cv': 'CV · Paradzai Gwira, AI Software Engineer',
  '/job-application': 'Job Application · Paradzai Gwira',
  '/pricing-order': 'Place an Order · Paradzai Gwira'
}

// Resets scroll and the tab title whenever the route changes (hash links scroll themselves).
export const RouteEffects = () => {
  const { pathname, hash } = useLocation()

  useEffect(() => {
    document.title = pageTitles[pathname] || 'Page not found · Paradzai Gwira'
    if (!hash) window.scrollTo({ top: 0, behavior: 'instant' })
  }, [pathname, hash])

  return null
}

// A ring that trails the pointer and swells over anything clickable. Desktop pointers only.
export const CursorFollower = () => {
  const dotRef = useRef(null)
  const ringRef = useRef(null)

  useEffect(() => {
    if (prefersReducedMotion() || !window.matchMedia('(pointer: fine)').matches) return undefined
    const dot = dotRef.current
    const ring = ringRef.current
    const target = { x: -100, y: -100 }
    const current = { x: -100, y: -100 }
    let frame = 0

    const loop = () => {
      current.x += (target.x - current.x) * 0.18
      current.y += (target.y - current.y) * 0.18
      ring.style.transform = `translate3d(${current.x}px, ${current.y}px, 0)`
      frame = requestAnimationFrame(loop)
    }

    const onMove = (event) => {
      target.x = event.clientX
      target.y = event.clientY
      dot.style.transform = `translate3d(${target.x}px, ${target.y}px, 0)`
      document.body.classList.add('cursor-ready')
    }
    const onOver = (event) => {
      const interactive = event.target.closest('a, button, [role="tab"], input, textarea, select, label, .pf-spot')
      ring.classList.toggle('is-hover', Boolean(interactive))
      ring.classList.toggle('is-text', Boolean(event.target.closest('input, textarea')))
    }
    const onDown = () => ring.classList.add('is-down')
    const onUp = () => ring.classList.remove('is-down')
    const onLeave = () => document.body.classList.remove('cursor-ready')

    frame = requestAnimationFrame(loop)
    window.addEventListener('pointermove', onMove, { passive: true })
    document.addEventListener('pointerover', onOver)
    window.addEventListener('pointerdown', onDown)
    window.addEventListener('pointerup', onUp)
    document.documentElement.addEventListener('pointerleave', onLeave)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerover', onOver)
      window.removeEventListener('pointerdown', onDown)
      window.removeEventListener('pointerup', onUp)
      document.documentElement.removeEventListener('pointerleave', onLeave)
    }
  }, [])

  return (
    <>
      <div className="cursor-ring" ref={ringRef} aria-hidden="true" />
      <div className="cursor-dot" ref={dotRef} aria-hidden="true" />
    </>
  )
}

// Elements marked `data-magnetic` lean toward the pointer, then spring back.
export const MagneticButtons = () => {
  useEffect(() => {
    if (prefersReducedMotion() || !window.matchMedia('(pointer: fine)').matches) return undefined
    let active = null

    const reset = (element) => {
      element.style.transform = ''
      element.classList.remove('is-magnetic')
    }
    const onMove = (event) => {
      const element = event.target.closest('[data-magnetic]')
      if (active && active !== element) reset(active)
      active = element
      if (!element) return
      const rect = element.getBoundingClientRect()
      const x = event.clientX - rect.left - rect.width / 2
      const y = event.clientY - rect.top - rect.height / 2
      element.classList.add('is-magnetic')
      element.style.transform = `translate(${x * 0.22}px, ${y * 0.32}px)`
    }
    const onLeaveWindow = () => { if (active) reset(active); active = null }

    window.addEventListener('pointermove', onMove, { passive: true })
    document.documentElement.addEventListener('pointerleave', onLeaveWindow)
    return () => {
      window.removeEventListener('pointermove', onMove)
      document.documentElement.removeEventListener('pointerleave', onLeaveWindow)
    }
  }, [])

  return null
}

const RING = 2 * Math.PI * 20

// Floating back-to-top button whose ring fills as you scroll.
export const BackToTop = () => {
  const [progress, setProgress] = useState(0)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight
      setProgress(max > 0 ? window.scrollY / max : 0)
      setVisible(window.scrollY > 700)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <button
      type="button"
      className={`back-to-top ${visible ? 'is-visible' : ''}`}
      onClick={() => window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' })}
      aria-label="Back to top"
      tabIndex={visible ? 0 : -1}
    >
      <svg viewBox="0 0 48 48" aria-hidden="true">
        <circle cx="24" cy="24" r="20" className="back-to-top__track" />
        <circle cx="24" cy="24" r="20" className="back-to-top__fill" style={{ strokeDasharray: RING, strokeDashoffset: RING * (1 - progress) }} />
      </svg>
      <i className="fas fa-arrow-up" />
    </button>
  )
}

// Remounts on every route so each page fades and lifts in.
export const PageTransition = ({ children }) => {
  const { pathname } = useLocation()
  return <div className="page-transition" key={pathname}>{children}</div>
}
